// 管理会社向けのステータス進行可否判定ユーティリティ
// このユーティリティは管理会社画面からのみ呼ばれることを想定しています。
import type { Deal, DealWithPrices } from "~/models/Deal";
import type { DealEstimationContext } from "~/models/DealEstimate";
import type { DealEstimationHeader } from "~/models/DealEstimationHeader";
import type { DealEstimationHistoryContext } from "~/models/DealEstimationHistory";
import type {
	DealCompletionReportImage,
	DealInspectionReportImage
} from "~/models/DealReportImage";
import type { ItandiAccount } from "~/models/ItandiAccount";
import {
	AWAITING_ACCEPTANCE,
	IN_CONSTRUCTION,
	IN_ESTIMATION,
	NOW_ACCEPTING,
	OWN_AWAITING_ACCEPTANCE,
	OWN_FINISHED,
	OWN_IN_CONSTRUCTION,
	OWN_IN_ESTIMATION,
	OWN_SEND_ESTIMATE,
	ownStatusMovingValidation,
	SEND_ESTIMATE,
	statusMovingValidation
} from "~/models/Status";

export interface StatusForwardDeps {
	completionImages: DealCompletionReportImage[] | null;
	dealEstimationHistories: DealEstimationHistoryContext | null;
	dealEstimations: DealEstimationContext | null;
	estimationHeaders: DealEstimationHeader[] | null | undefined;
	inspectionImages: DealInspectionReportImage[] | null;
}

export const forwardStatusValidation = (
	deal: Deal | DealWithPrices,
	account: ItandiAccount | null,
	deps: StatusForwardDeps
): boolean => {
	const {
		dealEstimations,
		dealEstimationHistories,
		inspectionImages,
		completionImages,
		estimationHeaders
	} = deps;

	if (!account) return false;
	if (deal.dealVendorCompanies.length === 0) return false;

	switch (deal.statusId) {
		case OWN_IN_ESTIMATION:
		case OWN_SEND_ESTIMATE:
		case OWN_IN_CONSTRUCTION:
		case OWN_AWAITING_ACCEPTANCE:
			return ownStatusMovingValidation[deal.statusId](account);
		case OWN_FINISHED:
			return false;

		case NOW_ACCEPTING:
			return statusMovingValidation[NOW_ACCEPTING](account);

		case IN_ESTIMATION: {
			if (!dealEstimations || !dealEstimationHistories || !inspectionImages)
				return false;
			return statusMovingValidation[IN_ESTIMATION](
				deal,
				dealEstimations,
				dealEstimationHistories,
				inspectionImages,
				account.isAdmin,
				true
			);
		}
		case SEND_ESTIMATE: {
			if (!estimationHeaders || !dealEstimations) return false;
			return statusMovingValidation[SEND_ESTIMATE](
				estimationHeaders,
				dealEstimations,
				true
			);
		}
		case IN_CONSTRUCTION: {
			if (
				!dealEstimations ||
				!dealEstimationHistories ||
				!inspectionImages ||
				!completionImages ||
				!estimationHeaders
			)
				return false;
			const requestHeader =
				estimationHeaders.find((h) => h.status === "request") ?? null;
			return statusMovingValidation[IN_CONSTRUCTION](
				deal,
				dealEstimations,
				dealEstimationHistories,
				requestHeader,
				inspectionImages,
				completionImages,
				false
			);
		}

		case AWAITING_ACCEPTANCE:
			return statusMovingValidation[AWAITING_ACCEPTANCE](account);

		default:
			return false;
	}
};
